import React from 'react';
import { cn } from '../../../lib/utils';
import type { CaseProfileModel } from './types';
import { ProfileChip } from './ProfileChip';

export interface CaseProfileBarProps {
  profile: CaseProfileModel;
  selectedIds: string[];
  onSelectedChange: (ids: string[]) => void;
  className?: string;
}

export function CaseProfileBar({
  profile,
  selectedIds,
  onSelectedChange,
  className,
}: CaseProfileBarProps) {
  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectedChange(selectedIds.filter((x) => x !== id));
    } else {
      onSelectedChange([...selectedIds, id]);
    }
  };

  const hasSelection = selectedIds.length > 0;

  return (
    <div
      className={cn(
        'w-full bg-[var(--color-bg-base)] px-3 py-1.5 flex flex-col gap-[4px]',
        className
      )}
      role="group"
      aria-label="Case profile"
    >
      {/* Row 1: TYPE */}
      <div className="flex items-center gap-2">
        <div className="w-16 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-label)]">
          TYPE
        </div>
        <div className="flex-1 flex items-center gap-2">
          {profile.typeChips.map((chip) => (
            <ProfileChip
              key={chip.id}
              chip={chip}
              selected={selectedIds.includes(chip.id)}
              onToggle={handleToggle}
            />
          ))}
        </div>
        {hasSelection && (
          <div className="flex items-center gap-3 ml-auto">
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-dim)]">
              FILTER · {selectedIds.length} SELECTED
            </span>
            <button
              type="button"
              onClick={() => onSelectedChange([])}
              className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-needs-human-text)] hover:text-[var(--color-brand-hover)] transition-colors duration-150"
            >
              CLEAR ×
            </button>
          </div>
        )}
      </div>

      {/* Row 2: EVIDENCE */}
      {profile.evidenceChips.length >= 1 && (
        <div className="flex items-center gap-2">
          <div className="w-16 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-label)]">
            EVIDENCE
          </div>
          <div className="flex-1 flex items-center gap-2">
            {profile.evidenceChips.map((chip) => (
              <ProfileChip
                key={chip.id}
                chip={chip}
                selected={selectedIds.includes(chip.id)}
                onToggle={handleToggle}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CaseProfileBar;
